import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { ukTime as ukDateTime } from "./oohUtils";
import { PaperPlaneTilt, ClockCounterClockwise } from "@phosphor-icons/react";

const mins = (s) => (s ? `${Math.floor(s / 60)} min ${s % 60}s` : "0 min");

export default function OohShiftSummaryPanel({ canManage, lastSentAt }) {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: h }] = await Promise.all([API.get("/ooh/shift-summary"), API.get("/ooh/shift-summaries", { params: { limit: 5 } })]);
      setPreview(p); setHistory(h.summaries); setFailed(false);
    } catch { setFailed(true); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    setBusy(true);
    try { const { data } = await API.post("/ooh/shift-summary/send", {}); toast.success(data.sent_to?.length ? `Shift summary emailed to ${data.sent_to.length} director${data.sent_to.length === 1 ? "" : "s"}` : "Shift summary recorded — no director email addresses found"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send the summary"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid="ooh-summary-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">08:00 shift summary for Operations Directors</p>
          <p className="text-xs text-muted-foreground">Sent automatically when the day team takes over: requests, call minutes, contractors called out, handovers and anything still open.{lastSentAt ? ` Last sent ${ukDateTime(lastSentAt)}.` : " Not sent yet."}</p>
        </div>
        {canManage && <Button size="sm" variant="outline" onClick={send} disabled={busy} data-testid="ooh-summary-send"><PaperPlaneTilt size={14} /> {busy ? "Sending…" : "Send last shift now"}</Button>}
      </div>
      {failed && <p className="text-xs text-red-600" data-testid="ooh-summary-error">Couldn't load the shift preview — try again in a moment.</p>}
      {preview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="ooh-summary-preview">
          {[["Requests", preview.received], ["Calls", `${preview.calls} · ${mins(preview.call_seconds)}`], ["Call-outs", preview.callouts.length], ["Handed over", preview.handovers.length]].map(([l, v]) => (
            <div key={l} className="rounded bg-muted/50 px-2 py-1.5"><p className="text-sm font-bold tabular-nums">{v}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p></div>
          ))}
          <p className="col-span-full text-[10px] text-muted-foreground text-left">Preview of the last shift: {ukDateTime(preview.since)} → {ukDateTime(preview.until)}{preview.on_call ? ` · on call ${preview.on_call}` : ""}</p>
        </div>
      )}
      {history.length > 0 && (
        <ul className="text-xs space-y-1" data-testid="ooh-summary-history">
          {history.map((h) => <li key={h.id} className="flex items-center gap-2 text-muted-foreground"><ClockCounterClockwise size={12} /> {ukDateTime(h.generated_at)} — {h.received} received · {h.calls} calls · {h.callouts?.length || 0} call-outs · {h.handovers?.length || 0} handed over · to {h.sent_to?.length || 0}{h.manual ? ` (sent by ${h.triggered_by})` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}
